import {
  type DailyWordProgress,
  daysRemaining,
  GOAL_DEADLINE_KEY,
  GOAL_WORD_COUNT_KEY,
  progressForToday,
  readWorkspaceGoals,
  WORDS_PER_DAY_GOAL_KEY,
} from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import {
  averageReadingDuration,
  formatPageCount,
  mergeWorkspaceDocTotals,
  normalizeWordsPerPage,
  readingEstimates,
} from '@bangle.io/service-core';
import { WorkspaceStatsStrip } from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import React from 'react';

const STATS_LABELS = {
  words: t.app.sidebar.stats.words,
  pages: t.app.sidebar.stats.pages,
  read: t.app.sidebar.stats.read,
  wordsDetails: t.app.sidebar.stats.wordsDetails,
  pagesDetails: t.app.sidebar.stats.pagesDetails,
  readDetails: t.app.sidebar.stats.readDetails,
  characters: t.app.sidebar.stats.characters,
  charactersNoSpaces: t.app.sidebar.stats.charactersNoSpaces,
  charactersNoSpacesSublabel: t.app.sidebar.stats.charactersNoSpacesSublabel,
  avgWordsPerDoc: t.app.sidebar.stats.avgWordsPerDoc,
  documents: t.app.sidebar.stats.documents,
  wordsPerPage: t.app.sidebar.stats.wordsPerPage,
  wordsPerPageHint: t.app.sidebar.stats.wordsPerPageHint,
  goals: t.app.sidebar.stats.goals,
  goalsDetails: t.app.sidebar.stats.goalsDetails,
  goalWordTarget: t.app.sidebar.stats.goalWordTarget,
  goalWordTargetHint: t.app.sidebar.stats.goalWordTargetHint,
  goalDeadline: t.app.sidebar.stats.goalDeadline,
  goalDeadlineHint: t.app.sidebar.stats.goalDeadlineHint,
  wordsPerDay: t.app.sidebar.stats.wordsPerDay,
  wordsPerDayHint: t.app.sidebar.stats.wordsPerDayHint,
  clearGoal: t.app.sidebar.stats.clearGoal,
  goalProgress: t.app.sidebar.stats.goalProgress,
  daysRemaining: t.app.sidebar.stats.daysRemaining,
  dailyProgress: t.app.sidebar.stats.dailyProgress,
} as const;

const READING_LABELS: Record<string, string> = {
  slow: t.app.sidebar.stats.readingSlow,
  average: t.app.sidebar.stats.readingAverage,
  fast: t.app.sidebar.stats.readingFast,
  aloud: t.app.sidebar.stats.readingAloud,
};

const DAILY_PROGRESS_KEY = 'dailyWordProgress';

function readStoredDailyProgress(
  metadata: Record<string, unknown> | undefined,
): DailyWordProgress | null {
  const raw = metadata?.[DAILY_PROGRESS_KEY];
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (
    typeof record.date !== 'string' ||
    typeof record.baselineWords !== 'number' ||
    typeof record.wordsWritten !== 'number'
  ) {
    return null;
  }
  return {
    date: record.date,
    baselineWords: record.baselineWords,
    wordsWritten: record.wordsWritten,
  };
}

/**
 * Sidebar Words / Pages / Read / Goals strip for the active workspace.
 */
export function WorkspaceStatsStripContainer() {
  const { editorEngine, workspaceOps, workspaceState } = useCoreServices();
  const activeWsName = useAtomValue(workspaceState.$currentWsName);
  const workspaces = useAtomValue(workspaceState.$workspaces);
  const contentStats = useAtomValue(workspaceState.$noteContentStats);
  const activeDocStats = useAtomValue(editorEngine.$activeDocStats);
  const activeWsPath = useAtomValue(workspaceState.$activeWsPaths)[0]?.wsPath;

  const [unsavedBodies, setUnsavedBodies] = React.useState(() =>
    editorEngine.getUnsavedBodies(),
  );

  React.useEffect(() => {
    return editorEngine.subscribeToSaveStatus(() => {
      setUnsavedBodies(new Map(editorEngine.getUnsavedBodies()));
    });
  }, [editorEngine]);

  const workspace = React.useMemo(
    () => workspaces.find((ws) => ws.name === activeWsName),
    [activeWsName, workspaces],
  );

  const wordsPerPage = React.useMemo(
    () => normalizeWordsPerPage(workspace?.metadata.wordsPerPage),
    [workspace],
  );

  const goalsMeta = React.useMemo(
    () => readWorkspaceGoals(workspace?.metadata),
    [workspace],
  );

  const totals = React.useMemo(
    () =>
      mergeWorkspaceDocTotals({
        contentStats,
        unsavedBodies,
        activeWsPath: activeDocStats?.wsPath ?? activeWsPath,
        activeDocStats: activeDocStats?.stats ?? null,
      }),
    [activeDocStats, activeWsPath, contentStats, unsavedBodies],
  );

  const dailyProgress = React.useMemo(
    () =>
      progressForToday({
        stored: readStoredDailyProgress(workspace?.metadata),
        currentWords: totals.words,
      }),
    [totals.words, workspace],
  );

  // Persist a refreshed daily baseline/progress when the day rolls or words grow.
  React.useEffect(() => {
    if (!activeWsName || !workspace) {
      return;
    }
    const stored = readStoredDailyProgress(workspace.metadata);
    if (
      stored &&
      stored.date === dailyProgress.date &&
      stored.baselineWords === dailyProgress.baselineWords &&
      stored.wordsWritten === dailyProgress.wordsWritten
    ) {
      return;
    }
    void workspaceOps.updateWorkspaceMetadata(activeWsName, (metadata) => ({
      ...metadata,
      [DAILY_PROGRESS_KEY]: dailyProgress,
    }));
  }, [activeWsName, dailyProgress, workspace, workspaceOps]);

  const readingRows = React.useMemo(
    () =>
      readingEstimates(totals.words).map((row) => ({
        id: row.id,
        label: READING_LABELS[row.id] ?? row.label,
        value: row.labelDuration,
        accent: row.id === 'average',
      })),
    [totals.words],
  );

  const handleWordsPerPageChange = React.useCallback(
    (next: number) => {
      if (!activeWsName) {
        return;
      }
      const normalized = normalizeWordsPerPage(next);
      void workspaceOps.updateWorkspaceMetadata(activeWsName, (metadata) => ({
        ...metadata,
        wordsPerPage: normalized,
      }));
    },
    [activeWsName, workspaceOps],
  );

  const patchGoal = React.useCallback(
    (key: string, value: number | string | null) => {
      if (!activeWsName) {
        return;
      }
      void workspaceOps.updateWorkspaceMetadata(activeWsName, (metadata) => {
        const next = { ...metadata };
        if (value == null) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    [activeWsName, workspaceOps],
  );

  if (!activeWsName) {
    return null;
  }

  return (
    <WorkspaceStatsStrip
      words={totals.words}
      chars={totals.chars}
      charsNoSpaces={totals.charsNoSpaces}
      docs={totals.docs}
      pagesValue={formatPageCount(totals.words, wordsPerPage)}
      readValue={averageReadingDuration(totals.words)}
      wordsPerPage={wordsPerPage}
      readingRows={readingRows}
      labels={STATS_LABELS}
      onWordsPerPageChange={handleWordsPerPageChange}
      goals={{
        goalWordCount: goalsMeta.goalWordCount,
        goalDeadline: goalsMeta.goalDeadline,
        wordsPerDayGoal: goalsMeta.wordsPerDayGoal,
        wordsWrittenToday: dailyProgress.wordsWritten,
        daysRemaining: goalsMeta.goalDeadline
          ? daysRemaining(goalsMeta.goalDeadline)
          : null,
      }}
      onGoalWordCountChange={(value) => patchGoal(GOAL_WORD_COUNT_KEY, value)}
      onGoalDeadlineChange={(value) => patchGoal(GOAL_DEADLINE_KEY, value)}
      onWordsPerDayGoalChange={(value) =>
        patchGoal(WORDS_PER_DAY_GOAL_KEY, value)
      }
    />
  );
}
